import utils from "../helpers/utils";
import api from "./api";
import { Appointment, CreateAppointmentPayload } from "../types";

async function getWeekAppointment(
  user_id: string,
  date = new Date()
): Promise<Appointment[]> {
  try {
    if (!user_id) return [];
    const { data } = await api.get(
      `/appointments/${user_id}/week/${utils.getFormattedDate(date)}/`
    );
    return data;
  } catch (e) {
    console.log(e);
    return [];
  }
}

async function getDayAppointment(
  user_id: string,
  date = new Date()
): Promise<Appointment[]> {
  try {
    if (!user_id) return [];
    const { data } = await api.get(
      `/appointments/${user_id}/day/${utils.getFormattedDate(date)}/`
    );
    return data;
  } catch (e) {
    console.log(e);
    return [];
  }
}

async function cancelAppointment(
  appointment_id: string,
  professional_id?: string
) {
  try {
    if (!professional_id) return [];
    const { data } = await api.delete(
      `/appointments/${professional_id}/${appointment_id}/cancel/`
    );
    return data;
  } catch (e) {
    console.log(e);
    return [];
  }
}

async function confirmAppointment(
  appointment_id: string,
  professional_id?: string
) {
  try {
    if (!professional_id) return [];
    const { data } = await api.post(
      `/appointments/${professional_id}/${appointment_id}/approve/`
    );
    return data;
  } catch (e) {
    console.log(e);
    return [];
  }
}

async function createAppointment(body: CreateAppointmentPayload) {
  try {
    await api.post(`/appointments/schedule/`, body);
    return true;
  } catch (e) {
    console.log(e);
    return false;
  }
}

async function updateAppointment(
  appointment_id: string,
  professional_id: string,
  body: CreateAppointmentPayload
) {
  try {
    await api.put(
      `/appointments/${professional_id}/${appointment_id}/update/`,
      body
    );
    return true;
  } catch (e) {
    console.log(e);
    return false;
  }
}

async function getAvailability(professional_id: string) {
  try {
    const { data } = await api.get(`/appointments/availability/${professional_id}/`);
    return {
      availabilities: data.availabilities || data || [],
      auto_block_scheduled: data.auto_block_scheduled ?? true,
    };
  } catch (e) {
    console.log(e);
    return { availabilities: [], auto_block_scheduled: true };
  }
}

async function setAvailability(
  professional_id: string,
  availabilities: unknown[],
  auto_block_scheduled?: boolean
) {
  try {
    const { data } = await api.post(`/appointments/availability/${professional_id}/`, {
      availabilities,
      auto_block_scheduled,
    });
    return data;
  } catch (e) {
    console.log(e);
    return false;
  }
}

async function getBlockedPeriods(professional_id: string) {
  try {
    const { data } = await api.get(`/appointments/blocked-periods/${professional_id}/`);
    return data;
  } catch (e) {
    console.log(e);
    return [];
  }
}

async function setBlockedPeriods(professional_id: string, blocked_periods: unknown[]) {
  try {
    const { data } = await api.post(`/appointments/blocked-periods/${professional_id}/`, {
      blocked_periods,
    });
    return data;
  } catch (e) {
    console.log(e);
    return false;
  }
}

async function getProfessionalServices(professional_id: string) {
  try {
    const { data } = await api.get(`/appointments/professional-services/${professional_id}/`);
    return data;
  } catch (e) {
    console.log(e);
    return [];
  }
}

async function getStudentAppointments(user_id: string): Promise<Appointment[]> {
  try {
    if (!user_id) return [];
    const { data } = await api.get(`/appointments/${user_id}/student/`);
    return data;
  } catch (e) {
    console.log(e);
    return [];
  }
}

async function getStudentAppointmentsReport(user_id: string) {
  try {
    if (!user_id) return null;
    const { data } = await api.get(`/appointments/${user_id}/student-report/`);
    return data;
  } catch (e) {
    console.log(e);
    return null;
  }
}

async function setProfessionalServices(professional_id: string, services: unknown[]) {
  try {
    const { data } = await api.post(`/appointments/professional-services/${professional_id}/`, {
      services,
    });
    return data;
  } catch (e) {
    console.log(e);
    return false;
  }
}

const appointment = {
  getWeekAppointment,
  getDayAppointment,
  cancelAppointment,
  confirmAppointment,
  createAppointment,
  updateAppointment,
  getAvailability,
  setAvailability,
  getBlockedPeriods,
  setBlockedPeriods,
  getProfessionalServices,
  setProfessionalServices,
  getStudentAppointments,
  getStudentAppointmentsReport,
};

export default appointment;
